import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Slide,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircleOutline,
  ErrorOutline,
  InfoOutlined,
  WarningAmberOutlined,
} from '@mui/icons-material';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Helper to determine semantic style based on the alert title/message
const getAlertConfig = (title, message) => {
  const textMatches = (`${title} ${message}`).toLowerCase();

  if (textMatches.includes('error') || textMatches.includes('fail') || textMatches.includes('cancel') || textMatches.includes('reject')) {
    return {
      icon: <ErrorOutline sx={{ fontSize: 48, color: 'error.main' }} />,
      btnColor: 'error',
      bgColor: '#fff0f0'
    };
  }
  if (textMatches.includes('success')) {
    return {
      icon: <CheckCircleOutline sx={{ fontSize: 48, color: 'success.main' }} />,
      btnColor: 'success',
      bgColor: '#f0fff4'
    };
  }
  if (textMatches.includes('warn') || textMatches.includes('please')) {
    return {
      icon: <WarningAmberOutlined sx={{ fontSize: 48, color: 'warning.main' }} />,
      btnColor: 'warning',
      bgColor: '#fffcf0'
    };
  }
  return {
    icon: <InfoOutlined sx={{ fontSize: 48, color: 'info.main' }} />,
    btnColor: 'primary',
    bgColor: '#f0f7ff'
  };
};

const AlertModal = ({ open, title = 'Alert', message, onClose }) => {
  const theme = useTheme();
  const config = getAlertConfig(title, message || '');

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      keepMounted
      maxWidth="xs"
      fullWidth
      sx={{ zIndex: (theme) => theme.zIndex.modal + 1100 }}
      PaperProps={{
        sx: {
          borderRadius: '16px',
          boxShadow: '0 12px 40px rgba(0,0,0,0.12)',
          overflow: 'hidden',
          m: 2,
          maxWidth: '360px',
        },
      }}
    >
      {/* Top Banner / Icon Area */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          pt: 3,
          pb: 1.5,
          bgcolor: config.bgColor,
          position: 'relative'
        }}
      >
        <Box sx={{
          bgcolor: '#fff',
          borderRadius: '50%',
          p: 1,
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
          display: 'flex',
          mb: -5,
          zIndex: 1
        }}>
          {config.icon}
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 12,
            top: 12,
            color: 'text.secondary',
            bgcolor: 'rgba(255,255,255,0.5)',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Content Area */}
      <DialogContent sx={{ px: 3, pt: 5, pb: 1.5, textAlign: 'center' }}>
        {title && title !== 'Alert' && (
          <Typography variant="h5" component="h2" sx={{ fontWeight: 800, mb: 1.5, color: 'text.primary', letterSpacing: '-0.02em', fontSize: '1.4rem' }}>
            {title}
          </Typography>
        )}
        <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.6, fontSize: '1.1rem' }}>
          {message}
        </Typography>
      </DialogContent>

      {/* Actions Area */}
      <DialogActions sx={{ px: 4, pb: 3, pt: 1, justifyContent: 'center' }}>
        <Button
          onClick={onClose}
          variant="contained"
          color={config.btnColor}
          disableElevation
          fullWidth
          sx={{
            py: 0.8,
            textTransform: 'none',
            fontSize: '1rem',
            fontWeight: 700,
            borderRadius: '12px',
            boxShadow: `0 8px 20px ${alpha(theme.palette[config.btnColor].main, 0.24)}`,
            '&:hover': {
              boxShadow: `0 6px 16px ${alpha(theme.palette[config.btnColor].main, 0.4)}`,
            }
          }}
        >
          OK
        </Button>
      </DialogActions>
    </Dialog >
  );
};

export default AlertModal;
